import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import DemoModal from '../../components/feature/DemoModal';

const HomePage = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Webcoll | Profesyonel Web Tasarım Hizmetleri İstanbul';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'İstanbul web tasarım hizmetleri Webcoll ile profesyonel web sitesi yaptırın. 3 günde yayında, SEO uyumlu, mobil responsive tasarım. 9.900 TL\'den başlayan fiyatlar.');
    }

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Webcoll",
      "description": "Profesyonel web tasarım ve geliştirme hizmetleri",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "logo": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+90-545-148-59-52",
        "contactType": "customer service",
        "availableLanguage": "Turkish",
        "areaServed": "TR"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "İstanbul",
        "addressCountry": "TR"
      },
      "sameAs": [
        "https://wa.me/905451485952"
      ],
      "service": [
        {
          "@type": "Service",
          "name": "Web Tasarım",
          "description": "Profesyonel web sitesi tasarım hizmetleri"
        },
        {
          "@type": "Service", 
          "name": "SEO Optimizasyonu",
          "description": "Arama motoru optimizasyonu hizmetleri"
        },
        {
          "@type": "Service",
          "name": "E-ticaret Çözümleri", 
          "description": "Online mağaza ve e-ticaret web sitesi hizmetleri"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section 
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/37bfaf48315374437f918d49ab014f7e.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-purple-900/80"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              Her pikselinde{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                profesyonellik
              </span>{' '}
              var
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-12 leading-relaxed">
              Webcoll, hız, estetik ve SEO'yu bir araya getirir. 3 günde yayında, 
              mobil uyumlu ve Google'da öne çıkan web siteleri.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => setIsDemoModalOpen(true)}
                className="px-10 py-5 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold text-lg rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-2xl whitespace-nowrap"
              >
                Hemen Başla
                <i className="ri-rocket-line ml-3 text-xl"></i>
              </button>
              <button
                onClick={() => setIsDemoModalOpen(true)}
                className="px-10 py-5 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white font-bold text-lg rounded-2xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 shadow-2xl whitespace-nowrap"
              >
                Canlı Demo Gör
                <i className="ri-play-circle-line ml-3 text-xl"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Webcoll'un <span className="text-blue-600">Farkı</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Neden binlerce işletme Webcoll'u tercih ediyor? İşte size özel avantajlarımız.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: '3 Günde Yayında',
                  description: 'Hızlı tasarım süreciyle web siteniz 3 iş günü içinde yayında. Zaman kaybetmeden dijital dünyada yerinizi alın.'
                },
                {
                  icon: 'ri-search-eye-line',
                  title: 'SEO Uyumlu ve Google Optimize',
                  description: 'Google\'da üst sıralarda yer almak için optimize edilmiş kodlama ve SEO stratejileri ile rakiplerinizin önüne geçin.'
                },
                {
                  icon: 'ri-smartphone-line',
                  title: 'Mobil & Tablet Uyumu',
                  description: 'Tüm cihazlarda mükemmel görünüm. Responsive tasarım ile müşterileriniz her yerden sizi bulabilir.'
                },
                {
                  icon: 'ri-palette-line',
                  title: 'Tasarım Kişiselleştirme',
                  description: 'Markanıza özel renk paleti, tipografi ve görsel kimlik. Sektörünüze uygun profesyonel tasarım.'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '7/24 Teknik Destek',
                  description: 'Uzman ekibimiz her zaman yanınızda. Teknik sorunlarınızı anında çözüyoruz, işiniz hiç durmuyor.'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Yüksek Performans & Güvenli Altyapı',
                  description: 'Hızlı yüklenen, güvenli ve kesintisiz çalışan web sitesi. SSL sertifikası ve güncel güvenlik önlemleri.'
                }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 hover:border-blue-200"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${feature.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Her bütçeye uygun <span className="text-purple-600">profesyonel çözümler</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                İşletmenize uygun paketi seçin, markanızı hemen yayına alın.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  name: 'Başlangıç',
                  price: '9.900',
                  period: 'TL',
                  description: 'Küçük işletmeler için ideal',
                  features: [
                    '5 Sayfa Web Sitesi',
                    '3 Günde Yayında',
                    'Mobil Uyumlu Tasarım',
                    'SEO Temel Optimizasyon',
                    'Ücretsiz SSL Sertifikası',
                    '1 Yıl Hosting + Domain',
                    'İletişim Formu',
                    'Google Maps Entegrasyonu'
                  ],
                  popular: false
                },
                {
                  name: 'Profesyonel',
                  price: '17.900',
                  period: 'TL',
                  description: 'Büyüyen işletmeler için',
                  features: [
                    '10 Sayfa Web Sitesi',
                    '3 Günde Yayında',
                    'Premium Tasarım',
                    'Gelişmiş SEO Optimizasyon',
                    'Ücretsiz SSL Sertifikası',
                    '1 Yıl Hosting + Domain',
                    'Blog Sistemi',
                    'Sosyal Medya Entegrasyonu',
                    'Google Analytics',
                    '3 Ay Ücretsiz Destek'
                  ],
                  popular: true
                },
                {
                  name: 'Kurumsal',
                  price: '29.900',
                  period: 'TL',
                  description: 'Büyük şirketler için',
                  features: [
                    'Sınırsız Sayfa',
                    '3 Günde Yayında',
                    'Özel Tasarım',
                    'Profesyonel SEO',
                    'Ücretsiz SSL Sertifikası',
                    '1 Yıl Hosting + Domain',
                    'E-ticaret Hazırlığı',
                    'Çoklu Dil Desteği',
                    'Admin Paneli',
                    'Gelişmiş Analitik',
                    '6 Ay Ücretsiz Destek',
                    'Öncelikli Destek'
                  ],
                  popular: false
                }
              ].map((plan, index) => (
                <div 
                  key={index}
                  className={`relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 ${
                    plan.popular ? 'border-purple-500 scale-105' : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-bold">
                        En Popüler
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    <div className="flex items-center justify-center mb-2">
                      <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-xl text-gray-600 ml-2">{plan.period}</span>
                    </div>
                    <p className="text-sm text-gray-500">Tek seferlik ödeme</p>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <i className="ri-check-line text-green-500 text-xl mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => setIsDemoModalOpen(true)}
                    className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 shadow-lg'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Hemen Başla
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-300 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-blue-300 rounded-full blur-3xl animate-pulse delay-500"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Hayalinizdeki web sitesi{' '}
              <span className="text-yellow-300">3 günde hazır!</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-12">
              Profesyonel ekibimizle tanışın, projenizi konuşalım ve hemen başlayalım. 
              Ücretsiz danışmanlık için iletişime geçin.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => setIsDemoModalOpen(true)}
                className="px-10 py-5 bg-white text-blue-600 font-bold text-lg rounded-2xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl whitespace-nowrap"
              >
                Ücretsiz Danışmanlık
                <i className="ri-phone-line ml-3 text-xl"></i>
              </button>
              <Link
                to="/iletisim"
                className="px-10 py-5 bg-transparent border-2 border-white text-white font-bold text-lg rounded-2xl hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
              >
                İletişime Geç
                <i className="ri-arrow-right-line ml-3 text-xl"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* WhatsApp Floating Button */}
        <div className="fixed bottom-6 right-6 z-50">
          <a
            href="https://wa.me/905451485952"
            target="_blank"
            rel="noopener noreferrer"
            className="w-16 h-16 bg-green-500 hover:bg-white/20 hover:backdrop-blur-sm hover:border hover:border-white/80 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 transform hover:scale-110 animate-pulse"
          >
            <i className="ri-whatsapp-line text-white text-2xl"></i>
          </a>
        </div>

        <Footer />
      </div>

      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
    </>
  );
};

export default HomePage;
